<script setup lang="ts">
import { equalYearMonthInfo } from '@/appLogic/interfaces/date';
import { changeDayChart } from '@/appLogic/view/orderChart';
import Chart from '@/components/atom/Chart.vue';
import { DAY_NUM_OF_BIZDAYS } from '@/coreDomainsModule/constants';
import { IOrderBasis, IOrderBasisNextMonthlyData } from '@/coreDomainsModule/entities/orderBasis';
import { AnnounceFactory } from '@/coreDomainsModule/factories/announceFactory';
import { ArrivalFactory } from '@/coreDomainsModule/factories/ArrivalFactory';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { OrderBasisService } from '@/coreDomainsModule/services/orderBasisService';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import { i18n } from '@/i18n';
import { computed, ref, watchEffect } from 'vue';

const t = i18n.t;

const TABLE_NUM_CELL_WIDTH = 105;

const NextMonthlyTableGridTemplateColumn = ['140px', ...new Array(6).fill(`${TABLE_NUM_CELL_WIDTH}px`)];
const PastMonthlyTableGridTemplateColumn = ['66px', ...new Array(12).fill(`${TABLE_NUM_CELL_WIDTH}px`)];

/**
 * コンポーネントのProps定義
 */
interface Props {
  basisData?: IOrderBasis;
  sourceOrderId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  basisData: undefined,
  sourceOrderId: undefined,
});

const chartData = ref<any>(undefined);
const chartRange = ref<[number, number]>([0, DAY_NUM_OF_BIZDAYS]);
const chartRangeStartLabel = computed(() => chartData.value?.labels?.[chartRange.value[0]] ?? getGraphRangeLabel(chartRange.value[0]));
const chartRangeEndLabel = computed(
  () => chartData.value?.labels?.[chartRange.value[1] - 1] ?? getGraphRangeLabel(chartRange.value[1] - 1)
);

/**
 * 以後月一覧
 */
const nextMonthlyDates = computed(() => {
  if (!props.basisData || props.basisData.nextMonthlyInfos.length === 0) return [];
  const startYearMonth = props.basisData.nextMonthlyInfos.reduce((acc, current) =>
    DateService.compareYearMonths(acc.orderBasisTargetYearMonth, current.orderBasisTargetYearMonth) === 1 ? current : acc
  ).orderBasisTargetYearMonth;
  return Array.from({ length: 6 }, (_, i) => DateService.getShiftedIYearMonthByMonths(startYearMonth.year, startYearMonth.month, i));
});

/**
 * 以後月次データ
 */
const nextMonthlyInfo = computed(() => {
  return nextMonthlyDates.value.map((month) =>
    props.basisData?.nextMonthlyInfos.find((pmi) => equalYearMonthInfo(pmi.orderBasisTargetYearMonth, month))
  );
});

/**
 * 以前月一覧
 */
const pastMonthlyDates = computed(() => {
  if (!props.basisData || props.basisData.pastMonthlyInfos.length === 0) return [];
  const endYearMonth = props.basisData.pastMonthlyInfos.reduce((acc, current) =>
    DateService.compareYearMonths(acc.orderBasisTargetYearMonth, current.orderBasisTargetYearMonth) === 1 ? acc : current
  ).orderBasisTargetYearMonth;
  return Array.from({ length: 12 }, (_, i) => DateService.getShiftedIYearMonthByMonths(endYearMonth.year, endYearMonth.month, -11 + i));
});

/**
 * 以前月次データ
 */
const pastMonthlyInfo = computed(() => {
  return pastMonthlyDates.value.map((month) =>
    props.basisData?.pastMonthlyInfos.find((pmi) => equalYearMonthInfo(pmi.orderBasisTargetYearMonth, month))
  );
});

const sourceOrderNote = computed(() => {
  return props.basisData?.sourceOrderInfos.find((info) => info.sourceOrderId === props.sourceOrderId)?.snapshotOrderNote?.trim() ?? '';
});
const hasSourceOrderNote = computed(() => sourceOrderNote.value.length > 0);

/**
 * 予入荷を計算して返す
 * @param nextMonthlyData - 以後月次データ
 */
const calcPreArrivalAmount = (nextMonthlyData: IOrderBasisNextMonthlyData | undefined) => {
  if (!nextMonthlyData) return '--';
  return nextMonthlyData.dailyInfos
    .reduce((acc, dailyInfo) => (dailyInfo.snapshotArrivalAmount != null ? (acc += dailyInfo.snapshotArrivalAmount) : acc), 0)
    .addComma();
};

/**
 * グラフ表示範囲の日付ラベル
 * @param offsetDays - シミュレーション開始日からの日数
 */
const getGraphRangeLabel = (offsetDays: number): string => {
  const baseDate = new Date(props.basisData?.snapshotExecutionDate ?? new Date().getTime());
  baseDate.setDate(baseDate.getDate() + offsetDays);

  const yearStr = baseDate.getFullYear();
  const monthStr = baseDate.getMonth() + 1;
  const dayStr = baseDate.getDate();
  let formatStr = 'YYYY-MM-DD';
  formatStr = formatStr.replace(/YYYY/g, yearStr.toString());
  formatStr = formatStr.replace(/MM/g, monthStr.toString());
  formatStr = formatStr.replace(/DD/g, dayStr.toString());
  return formatStr;
};

/**
 * グラフ表示範囲の開始位置を更新する
 * @param event - inputイベント
 */
const onInputChartRangeStart = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = Number(input.value);
  const clampedValue = Math.min(value, chartRange.value[1] - 1);
  chartRange.value[0] = clampedValue;
  input.value = clampedValue.toString();
};

/**
 * グラフ表示範囲の終了位置を更新する
 * @param event - inputイベント
 */
const onInputChartRangeEnd = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const value = Number(input.value);
  const clampedValue = Math.max(value, chartRange.value[0] + 1);
  chartRange.value[1] = clampedValue;
  input.value = clampedValue.toString();
};

watchEffect(async () => {
  // データがない場合はスキップ
  if (!props.basisData) {
    chartData.value = undefined;
    return;
  }

  // チャート描画
  // 登録済みの入荷snapshotをIArrivalに置き換える
  const expandedArrivalInfos = props.basisData.nextMonthlyInfos.flatMap((nextMonthlyInfo) =>
    nextMonthlyInfo.dailyInfos
      .map((nextDailyInfo) =>
        nextDailyInfo.snapshotArrivalAmount != null
          ? ArrivalFactory.create({
              amount: nextDailyInfo.snapshotArrivalAmount,
              crossDate: { ...nextMonthlyInfo.orderBasisTargetYearMonth, day: nextDailyInfo.orderBasisTargetDay },
              isFixOrdered: true,
            })
          : undefined
      )
      .filter((info) => info != null)
  );
  // 最も納期が後ろの発注済み予入荷を持っておく
  const lastExpandedArrivalInfo = expandedArrivalInfos
    .sort((a, b) => {
      if (!a.crossDate) return -1;
      if (!b.crossDate) return 1;
      return DateService.compareDates(a.crossDate, b.crossDate);
    })
    .at(-1);
  // Chart用のIBusinessを組み立てる
  const graphBusiness = BusinessFactory.create({
    package: props.basisData.snapshotPackage.toString(),
    inventory: props.basisData.snapshotLatestInventoryAmount,
    executionDate: new Date(props.basisData.snapshotExecutionDate),
    MOQ: props.basisData.snapshotMoq,
    customerAnnounceDaily: OrderBasisService.getCustomerAnnounceDailyForChart(props.basisData),
    customerAnnounce: props.basisData.nextMonthlyInfos
      .map((info) =>
        AnnounceFactory.create({
          year: info.orderBasisTargetYearMonth.year,
          month: info.orderBasisTargetYearMonth.month,
          amount: info.snapshotCustomerForecastAmount,
        })
      )
      .concat(
        props.basisData.pastMonthlyInfos.map((info) =>
          AnnounceFactory.create({
            year: info.orderBasisTargetYearMonth.year,
            month: info.orderBasisTargetYearMonth.month,
            amount: info.snapshotCustomerForecastAmount,
          })
        )
      ),
    stdInventoryIndices: { 標準在庫指数: props.basisData.userAdjustedStdInventoryIndex },
    recommend: expandedArrivalInfos,
  });
  // Chart用のExtraSimulateを組み立てる
  const graphExtraSimulate = {
    extraAnnounce: props.basisData.nextMonthlyInfos
      .map((info) =>
        AnnounceFactory.create({
          year: info.orderBasisTargetYearMonth.year,
          month: info.orderBasisTargetYearMonth.month,
          amount: info.userAdjustedCustomerForecastAmount,
        })
      )
      .concat(
        props.basisData.pastMonthlyInfos.map((info) =>
          AnnounceFactory.create({
            year: info.orderBasisTargetYearMonth.year,
            month: info.orderBasisTargetYearMonth.month,
            amount: info.snapshotCustomerForecastAmount,
          })
        )
      ),
    extraStartDate: lastExpandedArrivalInfo?.crossDate
      ? new Date(
          DateService.convertIDateToUnixTime(
            DateService.getShiftedIYearMonthByDays(
              lastExpandedArrivalInfo.crossDate.year,
              lastExpandedArrivalInfo.crossDate.month,
              lastExpandedArrivalInfo.crossDate.day,
              1
            )
          )
        )
      : new Date(props.basisData.snapshotExecutionDate),
    extraLeadTime: props.basisData.userAdjustedLeadTime.value,
    extraStdInventory: props.basisData.userAdjustedStdInventoryIndex,
    extraRecommend: expandedArrivalInfos,
  };
  // Chart用にシュミレートを回す
  chartData.value = await changeDayChart(
    graphBusiness,
    null,
    DAY_NUM_OF_BIZDAYS,
    0,
    DateService.convertDateToIDate(new Date(props.basisData.snapshotExecutionDate)),
    props.basisData.snapshotSimulationSetting,
    graphExtraSimulate
  );
});
</script>
<template>
  <div class="order-basis-detail">
    <div class="base__container">
      <div class="base__item">
        <div class="base__label">{{ t('商品') }}</div>
        <div class="base__value">{{ `${basisData?.snapshotBusinessNumber ?? '--'} / ${basisData?.snapshotBusinessName ?? '--'}` }}</div>
      </div>
      <div class="base__item">
        <div class="base__label">{{ t('仕入先') }}</div>
        <div class="base__value">{{ `${basisData?.snapshotSupplierNumber ?? '--'} / ${basisData?.snapshotSupplierName ?? '--'}` }}</div>
      </div>
      <div class="base__item">
        <div class="base__label">{{ t('容量') }}</div>
        <div class="base__value">{{ basisData?.snapshotPackage.addComma() }}</div>
      </div>
      <div class="base__item">
        <div class="base__label">{{ t('製造社') }}</div>
        <div class="base__value">{{ basisData?.snapshotManufacturer ?? '--' }}</div>
      </div>
      <div class="base__item">
        <div class="base__label">{{ t('MOQ') }}</div>
        <div class="base__value">{{ basisData?.snapshotMoq?.addComma() ?? '--' }}</div>
      </div>
      <div class="base__item">
        <div class="base__label">{{ t('発注単位') }}</div>
        <div class="base__value">{{ basisData?.snapshotOrderPackage?.addComma() ?? '--' }}</div>
      </div>
      <div class="base__item">
        <div class="base__label">{{ t('担当者') }}</div>
        <div class="base__value">{{ UserAccountService.getDisplayUsers(basisData?.snapshotResponsibleUsers ?? []) }}</div>
      </div>
    </div>
    <div class="graph__container">
      <Chart
        :date-unit="'day'"
        :styles="{
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
        }"
        :chart-data="chartData"
        chart-height="260px"
        :start="chartRange[0]"
        :end="chartRange[1]"
      />
      <div class="slidebar-multithumb">
        <input
          id="range-0"
          :value="chartRange[0]"
          class="thumb-0"
          type="range"
          name="range-0"
          min="0"
          :max="DAY_NUM_OF_BIZDAYS - 1"
          step="1"
          @input="onInputChartRangeStart"
        />
        <input
          id="range-1"
          :value="chartRange[1]"
          class="thumb-1"
          type="range"
          name="range-1"
          min="1"
          :max="DAY_NUM_OF_BIZDAYS"
          step="1"
          @input="onInputChartRangeEnd"
        />
        <span class="graph-range-left">{{ chartRangeStartLabel }}</span>
        <span class="graph-range-right">{{ chartRangeEndLabel }}</span>
      </div>
    </div>
    <div class="next-info__container">
      <EzTable class="table" :grid-template-columns="NextMonthlyTableGridTemplateColumn" sticky-header sticky-left-columns>
        <template #header>
          <EzHeader class="table--header table--left-header" />
          <template v-for="(header, index) in nextMonthlyDates" :key="`table-next-header-${index}`">
            <EzHeader class="table--header">
              {{ DateService.convertIYearMonthToYYYYMMWithSlash(header, true) }}
            </EzHeader>
          </template>
        </template>
        <template #body>
          <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell table--left-header" :content-padding="8">
              {{ t('予入荷') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number> {{ calcPreArrivalAmount(targetMonthBasis) }} </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell table--left-header" :content-padding="8">
              {{ t('オリジナル内示') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number>
                {{ targetMonthBasis?.snapshotCustomerForecastAmount.addComma() ?? '--' }}
              </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell table--left-header" :content-padding="8">
              {{ t('シミュレーション内示') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number>
                {{ targetMonthBasis?.userAdjustedCustomerForecastAmount.addComma() ?? '--' }}
              </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell table--left-header" :content-padding="8">
              {{ t('予末在庫') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number>
                {{ targetMonthBasis?.snapshotPreInventoryAmount.addComma() ?? '--' }}
              </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="NextMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell table--left-header" :content-padding="8">
              {{ t('在庫回転率') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in nextMonthlyInfo" :key="index">
              <EzCell class="table--cell" force-right>
                {{
                  targetMonthBasis && targetMonthBasis.snapshotCustomerForecastAmount !== 0
                    ? (targetMonthBasis.snapshotPreInventoryAmount / targetMonthBasis.snapshotCustomerForecastAmount).roundToDecimal(2)
                    : '--'
                }}
              </EzCell>
            </template>
          </EzTableRow>
        </template>
      </EzTable>
    </div>

    <div class="note__container">
      <div class="note__header">
        {{ t('メモ') }}
      </div>
      <p class="note__body" :class="{ 'note__body--empty': !hasSourceOrderNote }">
        {{ hasSourceOrderNote ? sourceOrderNote : t('メモはありません') }}
      </p>
    </div>

    <div class="past-info__container">
      <EzTable class="table" :grid-template-columns="PastMonthlyTableGridTemplateColumn" sticky-header>
        <template #header>
          <EzHeader class="table--header" sticky="left" />
          <template v-for="(header, index) in pastMonthlyDates" :key="`table-past-header-${index}`">
            <EzHeader class="table--header">{{ DateService.convertIYearMonthToYYYYMMWithSlash(header, true) }}</EzHeader>
          </template>
        </template>
        <template #body>
          <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell" :content-padding="8" sticky="left">
              {{ t('販売実績') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number>
                {{ targetMonthBasis?.snapshotSalesResultAmount.addComma() ?? '--' }}
              </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell" :content-padding="8" sticky="left">
              {{ t('内示') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number>
                {{ targetMonthBasis?.snapshotCustomerForecastAmount.addComma() ?? '--' }}
              </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell" :content-padding="8" sticky="left">
              {{ t('正答率') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
              <EzCell class="table--cell" force-right>
                {{
                  targetMonthBasis && targetMonthBasis.snapshotCustomerForecastAmount !== 0
                    ? ((targetMonthBasis.snapshotSalesResultAmount / targetMonthBasis.snapshotCustomerForecastAmount) * 100).roundToDecimal(
                        2
                      ) + '%'
                    : '--'
                }}
              </EzCell>
            </template>
          </EzTableRow>
          <EzTableRow :grid-template-columns="PastMonthlyTableGridTemplateColumn">
            <EzCell class="table--cell" :content-padding="8" sticky="left">
              {{ t('在庫') }}
            </EzCell>
            <template v-for="(targetMonthBasis, index) in pastMonthlyInfo" :key="index">
              <EzCell class="table--cell" is-number>
                {{ targetMonthBasis?.snapshotInventoryAmount.addComma() ?? '--' }}
              </EzCell>
            </template>
          </EzTableRow>
        </template>
      </EzTable>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.order-basis-detail {
  display: grid;
  gap: 8px;
  min-width: 0;
  grid-template-columns: max-content minmax(0, 1fr);
  grid-template-rows: 1fr;
  grid-template-areas:
    'base base'
    'graph graph'
    'next note'
    'past past';
}

.base {
  &__container {
    display: grid;
    grid-template-columns: 3fr 3fr 1fr 2fr 1fr 1fr 2fr;
    gap: 6px;
    padding: 8px;
    border-radius: 8px;
    background-color: $semanticWhite;
    min-width: 0;
    grid-area: base;
  }
  &__item {
    min-width: 0;
    display: grid;
    align-content: center;
    gap: 4px;
    padding: 6px 8px;
    border-radius: 6px;
    background-color: $surfaceDefault;
  }
  &__label {
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    color: $textSecondary;
    white-space: nowrap;
  }
  &__value {
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.25;
    color: $textDefault;
    overflow-wrap: anywhere;
  }
}

.graph__container {
  min-width: 0;
  grid-area: graph;
}

.slidebar-multithumb {
  margin: auto;
  display: grid;
  padding: 4px 12px;
  background-color: $semanticWhite;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

.slidebar-multithumb input[type='range'] {
  grid-column: 1;
  grid-row: 2;
  pointer-events: none;
  height: 10px;
  appearance: none;
  background: none;
  overflow: hidden;
}

.slidebar-multithumb ::-webkit-slider-thumb {
  pointer-events: auto;
  appearance: none;
  width: 1rem;
  height: 1rem;
}

.thumb-0 {
  z-index: 1;
}

.thumb-0::-webkit-slider-runnable-track {
  background: none;
}

.thumb-1::-webkit-slider-runnable-track {
  background: $surfaceDefault;
}

.thumb-0::-webkit-slider-thumb {
  height: 10px;
  background: $semanticPrimary;
}

.thumb-1::-webkit-slider-thumb {
  height: 10px;
  background: $semanticPrimary;
}

.graph-range-left {
  font-size: 12px;
  line-height: 1.2;
  grid-column: 1;
  grid-row: 3;
}

.graph-range-right {
  font-size: 12px;
  line-height: 1.2;
  grid-column: 1;
  grid-row: 3;
  text-align: right;
}

.next-info__container {
  min-width: 0;
  height: fit-content;
  overflow-x: auto;
  grid-area: next;
}

.note {
  &__container {
    min-width: 0;
    min-height: 0;
    display: grid;
    grid-template-rows: max-content minmax(0, 1fr);
    gap: 6px;
    padding: 8px;
    border-radius: 8px;
    background-color: $semanticWhite;
    grid-area: note;
  }
  &__header {
    color: $textSecondary;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
  }
  &__body {
    min-width: 0;
    min-height: 0;
    margin: 0;
    padding: 10px;
    border-radius: 6px;
    background-color: $surfaceDefault;
    color: $textDefault;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.45;
    overflow: auto;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    &--empty {
      color: $textSecondary;
      font-weight: 400;
    }
  }
}

.past-info__container {
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  grid-area: past;
}

.table {
  &--header {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    color: $textDefault;
    padding: 11px 0;
    white-space: nowrap;
  }
  &--left-header {
    position: sticky;
    left: 0;
    z-index: 5;
  }
  &--cell {
    font-family: Noto Sans JP;
    font-size: 18px;
    font-weight: 400;
    line-height: 1;
    color: $textSecondary;
    padding: 8px 0;
  }
}
</style>
